// src/pages/debitNote/DebitNoteDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, FileMinus, DollarSign, Printer, FileText, Building2, Calendar, User, Tag, RotateCcw } from 'lucide-react';
import { PageHeader, PrintPreviewModal, InfoCard } from '../../components/common';
import { getDebitNote, applyDebitNote, cancelDebitNote } from '../../services/api/procurement/debitNote';
import { getAllPurchaseOrders } from '../../services/api/purchaseOrder';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import { exportDebitNotePDF } from '../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../utils/themeColors';
import { PURCHASE_ORDER_BASE_PATH } from '../../utils/procurementNavigation';
import DebitNoteA4Document from './DebitNoteA4Document';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './DebitNoteDetailsPage.module.css';

const STATUS_COLORS = {
  issued: 'info',
  applied: 'success',
  cancelled: 'error',
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const DebitNoteDetailsPage = () => {
  const { debitNoteId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();

  const [debitNote, setDebitNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyPurchaseOrders, setApplyPurchaseOrders] = useState([]);
  const [applyPurchaseOrderId, setApplyPurchaseOrderId] = useState('');
  const [applyAmount, setApplyAmount] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  const loadDebitNote = async () => {
    try {
      setLoading(true);
      const result = await getDebitNote(debitNoteId);
      setDebitNote(result.debitNote);
      if (result.debitNote?.supplierId) {
        const ordersResult = await getAllPurchaseOrders({ supplierId: result.debitNote.supplierId, limit: 200 });
        // Balance derived from grandTotal − paidAmount so pre-existing orders with a stale
        // balanceAmount still show; the backend recalculates before applying.
        const openOrders = (ordersResult.purchaseOrders || [])
          .map(po => ({ ...po, openBalance: Math.max(0, parseFloat(po.grandTotal || 0) - parseFloat(po.paidAmount || 0)) }))
          .filter(po => po.status !== 'cancelled' && po.status !== 'draft' && po.openBalance > 0);
        setApplyPurchaseOrders(openOrders);
        const ownOrder = openOrders.find(po => po.id === result.debitNote.purchaseOrderId);
        setApplyPurchaseOrderId(ownOrder ? ownOrder.id : '');
      }
    } catch (err) {
      error(err?.response?.data?.message || t('debitNote.failedToLoadDebitNote'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDebitNote(); }, [debitNoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remainingAmount = Math.max(0, Number(debitNote?.amount || 0) - Number(debitNote?.appliedAmount || 0));
  const selectedApplyOrder = applyPurchaseOrders.find(po => po.id === Number(applyPurchaseOrderId));
  const applyCeiling = Math.min(remainingAmount, selectedApplyOrder ? selectedApplyOrder.openBalance : remainingAmount);

  useEffect(() => {
    if (applyCeiling > 0) setApplyAmount(String(Number(applyCeiling.toFixed(2))));
  }, [applyCeiling]);

  const handleApply = async () => {
    const amount = Number(applyAmount);
    if (!selectedApplyOrder) {
      error(t('debitNote.purchaseOrderRequired'));
      return;
    }
    if (!amount || amount <= 0) {
      error(t('debitNote.amountMustBePositive'));
      return;
    }
    if (amount > remainingAmount) {
      error(t('debitNote.applyAmountExceedsRemaining'));
      return;
    }
    if (amount > selectedApplyOrder.openBalance) {
      error(t('debitNote.applyAmountExceedsPurchaseOrderBalance'));
      return;
    }
    try {
      setIsApplying(true);
      await applyDebitNote(debitNoteId, { amount, purchaseOrderId: Number(applyPurchaseOrderId) });
      success(t('debitNote.debitNoteAppliedSuccessfully'));
      setApplyAmount('');
      await loadDebitNote();
    } catch (err) {
      error(err?.response?.data?.message || t('debitNote.failedToApplyDebitNote'));
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = await confirm(t('debitNote.confirmCancelDebitNote'));
    if (!confirmed) return;
    try {
      setIsCancelling(true);
      await cancelDebitNote(debitNoteId);
      success(t('debitNote.debitNoteCancelledSuccessfully'));
      await loadDebitNote();
    } catch (err) {
      error(err?.response?.data?.message || t('debitNote.failedToCancelDebitNote'));
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!debitNote) {
    return <div className={styles.notFound}>{t('errors.notFound')}</div>;
  }

  const handlePrint = () => setShowPrint(true);
  const canApply = remainingAmount > 0 && debitNote.status !== 'cancelled';
  const canCancel = hasPermission('issue_debit_note') && debitNote.status !== 'cancelled' && Number(debitNote.appliedAmount) === 0;

  return (
    <div className={styles.page}>
      <PageHeader
        className={styles.detailHeader}
        title={debitNote.debitNoteNumber}
        icon={null}
        breadcrumbs={
          <button onClick={() => navigate('/procurement/debit-notes')} className={styles.backBtn}>
            <ChevronLeft size={16} /> {t('debitNote.allDebitNotes')}
          </button>
        }
        actions={
          <div className={styles.applyActions}>
            <button type="button" className={styles.secondaryBtn} onClick={handlePrint}>
              <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
            </button>
            {hasPermission('issue_debit_note') && canApply && (
              <>
                <div className={styles.orderSelectWrapper}>
                  <Dropdown
                    value={applyPurchaseOrderId}
                    onChange={value => setApplyPurchaseOrderId(value || '')}
                    options={applyPurchaseOrders.map(po => ({ value: po.id, label: `${po.purchaseOrderNumber} (${formatCurrency(po.openBalance)})` }))}
                    placeholder={t('debitNote.selectPurchaseOrder')}
                    allowClear={false}
                    searchable
                    size="compact"
                  />
                </div>
                <input
                  type="number"
                  min="0.01"
                  max={applyCeiling}
                  step="0.01"
                  value={applyAmount}
                  onChange={event => setApplyAmount(event.target.value)}
                  placeholder={t('debitNote.applyAmountLabel')}
                  aria-label={t('debitNote.applyAmountLabel')}
                />
                <button type="button" className={styles.applyButton} onClick={handleApply} disabled={isApplying || !selectedApplyOrder}>
                  {t('debitNote.applyDebitNote')}
                </button>
              </>
            )}
            {canCancel && (
              <button type="button" className={styles.cancelButton} onClick={handleCancel} disabled={isCancelling}>
                {t('debitNote.cancelDebitNote')}
              </button>
            )}
          </div>
        }
      />

      <div className={styles.grid}>
        <div className={styles.column}>
          <InfoCard icon={FileMinus} title={t('debitNote.debitNoteNumber')}>
            <div className={ps.infoGrid}>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('common.status')}</span>
                <span className={ps.infoValue}>
                  <Badge type={STATUS_COLORS[debitNote.status]} label={t(`debitNote.status_${debitNote.status}`)} />
                </span>
              </div>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('debitNote.supplier')}</span>
                <span className={ps.infoValue}><Building2 size={14} />{debitNote.Supplier?.name || t('common.na')}</span>
              </div>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('debitNote.purchaseOrderNumber')}</span>
                <span className={ps.infoValue}>
                  <FileText size={14} />
                  {debitNote.purchaseOrder?.purchaseOrderNumber ? (
                    <Link to={`${PURCHASE_ORDER_BASE_PATH}/${debitNote.purchaseOrder.id}`}>{debitNote.purchaseOrder.purchaseOrderNumber}</Link>
                  ) : t('common.na')}
                </span>
              </div>
              {debitNote.purchaseOrder && (
                <div className={ps.infoItem}>
                  <span className={ps.infoLabel}>{t('debitNote.purchaseOrderBalance')}</span>
                  <span className={`${ps.infoValue} ${ps.amountCell}`}>
                    {formatCurrency(debitNote.purchaseOrder.balanceAmount)} / {formatCurrency(debitNote.purchaseOrder.grandTotal)}
                  </span>
                </div>
              )}
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('debitNote.sourceType')}</span>
                <span className={ps.infoValue}><Tag size={14} />{t(`debitNote.sourceType_${debitNote.sourceType}`)}</span>
              </div>
              {debitNote.sourceReturnToVendor && (
                <div className={ps.infoItem}>
                  <span className={ps.infoLabel}>{t('debitNote.sourceReturnToVendor')}</span>
                  <span className={ps.infoValue}>
                    <RotateCcw size={14} />
                    <Link to={`/procurement/return-to-vendor/${debitNote.sourceReturnToVendorId}`}>
                      {debitNote.sourceReturnToVendor.rtvNumber}
                    </Link>
                  </span>
                </div>
              )}
              {debitNote.reason && (
                <div className={`${ps.infoItem} ${ps.fullWidth}`}>
                  <span className={ps.infoLabel}>{t('debitNote.reason')}</span>
                  <p className={ps.notesText}>{debitNote.reason}</p>
                </div>
              )}
            </div>
          </InfoCard>

          <InfoCard icon={FileText} title={t('ecomOrders.additionalInformation')}>
            <div className={ps.infoList}>
              {debitNote.issuedBy?.name && (
                <div className={ps.infoListItem}>
                  <label>{t('returnToVendor.createdBy')}</label>
                  <p><User size={14} />{debitNote.issuedBy.name}</p>
                </div>
              )}
              <div className={ps.infoListItem}>
                <label>{t('common.created')}</label>
                <p><Calendar size={14} />{formatDateTime(debitNote.createdAt)}</p>
              </div>
            </div>
          </InfoCard>
        </div>

        <InfoCard icon={DollarSign} title={t('debitNote.amount')}>
          <div className={ps.infoGrid}>
            <div className={`${ps.infoItem} ${ps.fullWidth}`}>
              <span className={ps.infoLabel}>{t('debitNote.amount')}</span>
              <span className={`${ps.infoValue} ${ps.infoValueLarge} ${ps.infoValueDanger}`}>{formatCurrency(debitNote.amount)}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('debitNote.appliedAmount')}</span>
              <span className={`${ps.infoValue} ${ps.amountCell}`}>{formatCurrency(debitNote.appliedAmount)}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('debitNote.remainingAmount')}</span>
              <span className={`${ps.infoValue} ${ps.amountCell}`}>{formatCurrency(remainingAmount)}</span>
            </div>
          </div>

          {(debitNote.offsetPayments || []).length > 0 && (
            <div className={styles.offsetBlock}>
              <span className={ps.infoLabel}>{t('debitNote.offsetPayments')}</span>
              <div className={ps.tableWrapper}>
                <table className={ps.itemsTable}>
                  <tbody>
                    {debitNote.offsetPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td><span className={ps.infoValue}><Calendar size={14} />{formatDate(payment.paymentDate || payment.paidAt)}</span></td>
                        <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{formatCurrency(payment.paymentAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </InfoCard>
      </div>

      {showPrint && (
        <PrintPreviewModal
          title={`Debit Note — ${debitNote.debitNoteNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Debit_Note_${debitNote.debitNoteNumber || debitNote.id}.pdf`}
          onDownloadPdf={() => exportDebitNotePDF(debitNote, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderA4={() => <DebitNoteA4Document debitNote={debitNote} />}
        />
      )}
    </div>
  );
};

export default DebitNoteDetailsPage;
